import React from "react";
import { Plus, Pencil, Trash2, Send } from "lucide-react";
import {
  MaintenanceAreaDto,
  maintenanceAreasApi,
} from "../../../services/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AreaFormDialog } from "./AreaFormDialog";

export const AreasListPage: React.FC = () => {
  const [areas, setAreas] = React.useState<MaintenanceAreaDto[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [editing, setEditing] = React.useState<MaintenanceAreaDto | null>(null);
  const [creating, setCreating] = React.useState(false);

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await maintenanceAreasApi.getAll(true);
      setAreas(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void reload(); }, [reload]);

  const handleDelete = async (area: MaintenanceAreaDto) => {
    const cascadeMsg = area.equipmentCount > 0
      ? `\n\nSe desactivarán también sus ${area.equipmentCount} equipo(s) y todas sus tareas asociadas.`
      : "";
    if (!window.confirm(`¿Eliminar el área "${area.name}"?${cascadeMsg}\n\nEs un borrado lógico: podrás recuperarla desde administración si hace falta.`)) return;
    try {
      await maintenanceAreasApi.delete(area.id);
      await reload();
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
  };

  const handleTestDiscord = async (area: MaintenanceAreaDto) => {
    try {
      const result = await maintenanceAreasApi.testDiscord(area.id);
      window.alert(result.sent
        ? `Mensaje de prueba enviado al canal de "${area.name}". Comprueba Discord.`
        : `No se pudo enviar el mensaje. Revisa que la URL del webhook sea válida y esté activa.`);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-600">
            Cada área agrupa equipos y dirige los avisos a un canal de Discord concreto vía webhook.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4 mr-2" /> Nueva área
        </Button>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Cargando…</div>
      ) : areas.length === 0 ? (
        <Card className="p-6 text-center text-slate-500 text-sm">
          Aún no hay áreas. Crea la primera para empezar a registrar equipos.
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {areas.map((area) => (
            <Card key={area.id} className={`p-4 space-y-3 ${!area.isActive ? "opacity-60" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold">{area.name}</h3>
                  {area.description && (
                    <p className="text-sm text-slate-600 mt-0.5">{area.description}</p>
                  )}
                </div>
                {!area.isActive && (
                  <span className="text-xs rounded bg-slate-200 px-2 py-0.5">Inactiva</span>
                )}
              </div>

              <div className="text-xs text-slate-500 space-y-1">
                <div>
                  <span className="font-medium text-slate-700">Discord:</span>{" "}
                  {area.discordWebhookUrl ? (
                    <span className="text-green-700">Webhook configurado</span>
                  ) : (
                    <span className="text-amber-600">Sin configurar</span>
                  )}
                </div>
                <div>
                  <span className="font-medium text-slate-700">Equipos activos:</span>{" "}
                  {area.equipmentCount}
                </div>
              </div>

              <div className="flex gap-2 pt-2 border-t">
                <Button size="sm" variant="outline" onClick={() => setEditing(area)}>
                  <Pencil className="size-3.5 mr-1.5" /> Editar
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!area.discordWebhookUrl}
                  onClick={() => handleTestDiscord(area)}
                >
                  <Send className="size-3.5 mr-1.5" /> Probar Discord
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="ml-auto text-red-600 hover:text-red-700"
                  onClick={() => handleDelete(area)}
                >
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {(creating || editing) && (
        <AreaFormDialog
          area={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); void reload(); }}
        />
      )}
    </div>
  );
};
